import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { Product, ProductType } from '../../ts/interfaces/interfaces'
import { Payment, PaymentType } from '../../ts/interfaces/request'
import { createSelector } from 'reselect'
import { RootState } from '../index'

const selectSubscription = (state: RootState) => state.subscription
const selectInvoices = (state: RootState) => state.invoices

export const selectSubscriptionActive = createSelector([selectSubscription, selectInvoices], (subscription, invoices): boolean => {
  const { payment } = subscription

  return payment && payment.type !== PaymentType.FREE_TRIAL && invoices.active ? true : false
})

export const selectSubscriptionLoaded = createSelector([selectSubscription], (subscription): boolean => {
  return subscription.products.length > 0
})

export const selectHasBudgets = createSelector([selectSubscription], (subscription): boolean => {
  return subscription.products.some((p) => p.type === ProductType.budgets)
})

export const selectHasExpenseReviews = createSelector([selectSubscription], (subscription): boolean => {
  return subscription.products.some((p) => p.type === ProductType.expense_reviews)
})

export const selectHasInvoices = createSelector([selectSubscription], (subscription): boolean => {
  return subscription.products.some((p) => p.type === ProductType.invoices)
})

export const selectHasBankReconciliation = createSelector([selectSubscription], (subscription): boolean => {
  return subscription.products.some((p) => p.type === ProductType.bank_statement_reconciliation)
})

interface InitialState {
  products: Array<Product>
  payment: Payment | null
  hasInvoicesNoExpenses: boolean
}

const initialState: InitialState = {
  products: [],
  payment: null,
  hasInvoicesNoExpenses: false
}

function calcInvoicesNoExpenses(products: Product[]): boolean {
  const hasInvoices = products.some((p) => p.type === ProductType.invoices)
  const hasExpenses = products.some((p) => p.type === ProductType.expenses)
  return hasInvoices && !hasExpenses
}

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setSubscription: (state, action: PayloadAction<Partial<Omit<InitialState, 'hasInvoicesNoExpenses'>>>) => {
      if (action.payload.products) {
        state.products = action.payload.products
        state.hasInvoicesNoExpenses = calcInvoicesNoExpenses(state.products)
      }
      if (action.payload.payment !== undefined) {
        state.payment = action.payload.payment
      }
    }
  }
})

export const { setSubscription } = subscriptionSlice.actions

export default subscriptionSlice.reducer
